import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { loginSchema, type LoginFormData } from '../../schemas/auth.schemas';
import { useLogin } from '../../hooks/useLogin';
import { useGoogleLogin } from '../../hooks/useGoogleLogin';

function FormDivider(): React.JSX.Element {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
      </div>
    </div>
  );
}

export function LoginForm(): React.JSX.Element {
  const { login, isLoading: isLoginLoading } = useLogin();
  const { googleLogin, isLoading: isGoogleLoading } = useGoogleLogin();
  const isLoading = isLoginLoading || isGoogleLoading;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (data: LoginFormData): void => {
    login(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" placeholder="name@example.com" {...register('email')} />
        {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="Enter password"
          {...register('password')}
        />
        {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoginLoading ? 'Signing in...' : 'Sign In'}
      </Button>

      <FormDivider />

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => googleLogin()}
        disabled={isLoading}
      >
        {isGoogleLoading ? 'Signing in...' : 'Continue with Google'}
      </Button>
    </form>
  );
}
